import { t } from "@lingui/macro";
import * as Sentry from "@sentry/react";
import type { Currency } from "@src/__generated__/graphql";
import {
  AccessTokenForBudgetovacDocument,
  type AccessTokenForBudgetovacQuery,
  type AccessTokenForBudgetovacQueryVariables,
} from "@src/__generated__/urql-graphql";
import { BUDGETOVAC_API_URL } from "@src/config/constants";
import { appStore } from "@src/stores/AppStore";
import { nominate } from "@src/utils/formatters";
import { client } from "./client";

class BudgetovacAPI {
  private allfredAccessToken: string | null = null;
  private budgetovacAccessToken: string | null = null;
  private user: BudgetovacUser | null = null;

  public get isAuthenticated(): boolean {
    return !!this.budgetovacAccessToken;
  }

  private async getAllfredToken(): Promise<string | null> {
    if (this.allfredAccessToken) return this.allfredAccessToken;

    const { data } = await client
      .query<
        AccessTokenForBudgetovacQuery,
        AccessTokenForBudgetovacQueryVariables
      >(AccessTokenForBudgetovacDocument, {})
      .toPromise();

    if (!data?.getAccessTokenForThirdParty) return null;

    this.allfredAccessToken = data.getAccessTokenForThirdParty;
    return data.getAccessTokenForThirdParty;
  }

  private async getBudgetovacToken(): Promise<{
    token: string | null;
    user: BudgetovacUser | null;
  } | null> {
    if (this.budgetovacAccessToken && this.user) {
      return {
        token: this.budgetovacAccessToken,
        user: this.user,
      };
    }

    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");

      const res = await this.internalClient("/users/login-with-access-token", {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          access_token: await this.getAllfredToken(),
        }),
      });

      let token: string | null = null;
      let user: BudgetovacUser | null = null;

      await res.json().then(({ data }) => {
        token = data.token;
        user = data.user;
      });

      this.budgetovacAccessToken = token;
      this.user = user;

      return {
        token,
        user,
      };
    } catch (e) {
      console.error("BudgetovacAPI Error: ", e);
    }

    return null;
  }

  private internalClient(apiUrl: string, init: RequestInit) {
    return fetch(`${BUDGETOVAC_API_URL}/v1${apiUrl}`, init);
  }

  private async client(url: string, method: RequestInit["method"], body?: any) {
    const data = await this.getBudgetovacToken();

    if (!data?.token) {
      throw new Error("BudgetovacAPI: No token");
    }

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    // eslint-disable-next-line lingui/no-unlocalized-strings
    headers.append("Authorization", `Bearer ${data.token}`);

    return this.internalClient(url, {
      method: method,
      headers: headers,
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        return method === "GET" ? await res.json() : res;
      })
      .catch((error) => {
        console.error(error);
        Sentry.captureEvent({
          message: `Budgetovac ERROR: ${error.message}`,
        });
      });
  }

  public async updateArchivedStatus(id: string, archived: boolean) {
    return this.client(
      `/budgets/update-proposal-archived-status/${id}`,
      "POST",
      {
        status: archived,
      },
    );
  }

  private async canApproveBudget(
    type: "internal" | "client",
    amount: number,
    currency: Currency | undefined | null,
  ): Promise<boolean> {
    const data = await this.getBudgetovacToken();
    const user = data?.user;

    if (!user?.permissions) return false;
    if (
      user.permissions.includes(
        `budgets:approve_budgets_${type}_version_without_limit`,
      )
    ) {
      return true;
    }

    console.log(type, amount, currency);

    return (
      user.permissions.includes(`budgets:approve_budgets_${type}_version`) &&
      (user.budget_approval_limit === null ||
        user.budget_approval_limit >= nominate(amount, currency ?? 2))
    );
  }

  public async approveInternally(
    id: string,
    approved: boolean,
    amount: number,
    currency: Currency | undefined | null,
  ) {
    const canApprove = await this.canApproveBudget(
      "internal",
      amount,
      currency,
    );
    if (!canApprove) {
      appStore.UIStore.toast({
        status: "error",
        title: t`Insufficient permissions.`,
      });
      return { permissionError: true };
    }

    return this.client(`/budgets/approve-internal-version/${id}`, "POST", {
      status: approved,
    });
  }

  public async approveClient(
    id: string,
    approved: boolean,
    amount: number,
    currency: Currency | undefined | null,
  ) {
    const canApprove = await this.canApproveBudget("client", amount, currency);
    if (!canApprove) {
      appStore.UIStore.toast({
        status: "error",
        title: t`Insufficient permissions.`,
      });
      return { permissionError: true };
    }

    return this.client(`/budgets/approve-client-version/${id}`, "POST", {
      status: approved,
    });
  }

  public async deleteBudget(id: string) {
    return this.client(`/budgets/${id}`, "DELETE");
  }

  public async downloadBudget(id: string, language: string) {
    return this.client(`/budgets/download/xlsx/${id}/${language}`, "GET");
  }

  public async getTemplates(): Promise<{
    data: Array<{ id: number; name: string; version: number }>;
  }> {
    return this.client("/budgets/templates", "GET");
  }

  public assignedBudgetGroupsByProject(projectId: string) {
    return this.client(
      `/budgets/assigned-budgets-groups-by-project/${projectId}`,
      "GET",
    );
  }

  public async assign(id: string) {
    return this.client(`/budgets/assign-budget/${id}`, "POST");
  }

  public async unassign(id: string) {
    return this.client(`/budgets/unassign-budget/${id}`, "GET");
  }

  public async updateBudgetName(
    name: string,
    project_id: number,
    budget_number: number,
  ) {
    return this.client("/budgets/set-budget-name", "POST", {
      name,
      budget_number,
      project_id,
    });
  }

  public async updateDescription(id: string, description: string) {
    return this.client(`/budgets/update-budget-description/${id}`, "POST", {
      description,
    });
  }
}

export const budgetovacApi = new BudgetovacAPI();

type BudgetovacUser = {
  id: number | null;
  email: string | null;
  email_verified_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  phone: string | null;
  data: {
    account_manager: string;
  } | null;
  name: string | null;
  surname: string | null;
  enabled: boolean;
  deleted_at: string | null;
  role_id: string | null;
  rl_user: boolean;
  super_admin: boolean;
  projektovac_user_id: string | null;
  permissions: string[] | null;
  budget_approval_limit: number | null;
  user_role: {
    id: number | null;
    name: string | null;
    permissions: string[] | null;
    created_at: string | null;
    updated_at: string | null;
    protected: boolean;
  } | null;
};
